import React, { useEffect, useMemo } from 'react';
import { useForm, z, zodResolver } from '@fiverr-private/forms';
import { Container, Stack } from '@fiverr-private/layout_components';
import { DmBriefNeedType } from '@fiverr-private/dynamic_matching/dist/esm/src/gql-operations/gql-generated';
import { Gql } from '@fiverr-private/dynamic_matching';
import { Text } from '@fiverr-private/typography';
import { AppI18n, appTranslate } from '../../../i18n';
import { centsToCurrency } from '../Budget/formatCurrency';
import { EditActions } from '../EditActions';
import { useBriefCreationContext } from '../../../context/BriefCreationContext';
import { AdditionalWorkCheckbox } from '../NeedType/AdditionalWorkCheckbox';
import { BudgetRecommendedEducation, FixPriceInput } from './Budget';
import { getBudgetInCents } from './BudgetAndTimelineSection';
import {
  useBudgetAndTimelineContext,
  BudgetAndTimelineContent,
  hasOneTimeFixedPriceShape,
  hasOngoingFixedPriceShape,
  useSetOngoingTimeline,
} from './utils';
import {
  DeliveriesFrequencySelector,
  DeliveryTimelineSelector,
  HiringDurationSelector,
  isInvalidFrequencyValue,
} from './Timeline';

const copyPrefix = 'sections.budget_and_timeline';

export const FixedPriceEditComponent = ({ canChooseHourly }: { canChooseHourly: boolean }) => {
  const { currency } = useBriefCreationContext();

  const {
    sectionContentState,
    setSectionContentState,
    validMinBudget,
    validMaxBudget,
    needType,
    onEditEnd,
    onCancel,
    recommendedMinBudget,
    ongoingWorkPotential,
    setOngoingWorkPotential,
    additionalWorkPotentialAvailable,
  } = useBudgetAndTimelineContext();

  const { budgetInCents } = sectionContentState;
  const hasValidBudget = !!budgetInCents && budgetInCents >= validMinBudget && budgetInCents <= validMaxBudget;

  useEffect(() => {
    const run = async () => {
      const budgetValue = budgetInCents ? (budgetInCents / 100).toString() : '';

      methods.reset({ budget: budgetValue });
      methods.clearErrors();
      await methods.trigger();
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionContentState.pricingType]);

  useSetOngoingTimeline({ sectionContentState, setSectionContentState, needType });

  const schema = useMemo(
    () =>
      z.object({
        budget: z
          .string()
          .refine(
            (value) =>
              value === '' ||
              (getBudgetInCents(value, currency) >= validMinBudget &&
                getBudgetInCents(value, currency) <= validMaxBudget),
            {
              message: appTranslate(`${copyPrefix}.invalid_fixed_rate`, {
                params: {
                  min: centsToCurrency(validMinBudget, currency),
                  max: centsToCurrency(validMaxBudget, currency),
                },
              }).toString(),
            }
          ),
      }),
    [validMinBudget, validMaxBudget, currency]
  );

  const methods = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const validateAndChange = async (updatedValue: BudgetAndTimelineContent) => {
    if (await methods.trigger('budget')) {
      setSectionContentState(updatedValue);
    }
  };

  const isDisabled = () => {
    const sectionContentHasValidShape =
      needType === Gql.DmBriefNeedType.OneTime
        ? hasOneTimeFixedPriceShape(sectionContentState)
        : hasOngoingFixedPriceShape(sectionContentState);

    const isFrequencyInvalid = needType === DmBriefNeedType.Ongoing && isInvalidFrequencyValue(sectionContentState);

    return !hasValidBudget || !sectionContentHasValidShape || isFrequencyInvalid;
  };

  return (
    <Stack direction="column" gap={canChooseHourly ? '12' : '8'} width="100%">
      <Stack direction="column" gap="2">
        <Stack>
          <Stack>
            <FixPriceInput validateAndChange={validateAndChange} methods={methods} maxWidth="240px" />
          </Stack>
          {needType === Gql.DmBriefNeedType.Ongoing && (
            <Stack alignItems="flexEnd" paddingY="2">
              <Text>
                /
                <AppI18n k={`${copyPrefix}.delivery_timeline.delivery`} />
              </Text>
            </Stack>
          )}
        </Stack>
        {recommendedMinBudget &&
          hasValidBudget &&
          budgetInCents < recommendedMinBudget &&
          needType === Gql.DmBriefNeedType.OneTime && (
            <BudgetRecommendedEducation minimumPrice={recommendedMinBudget} currency={currency} />
          )}
      </Stack>
      {!needType || needType === Gql.DmBriefNeedType.OneTime ? (
        <Stack>
          <DeliveryTimelineSelector />
        </Stack>
      ) : (
        <>
          <Stack>
            <DeliveriesFrequencySelector />
          </Stack>
          <Stack>
            <HiringDurationSelector />
          </Stack>
        </>
      )}
      <Container>
        {additionalWorkPotentialAvailable && (
          <Container marginBottom="6">
            <AdditionalWorkCheckbox checked={ongoingWorkPotential} onChange={setOngoingWorkPotential} />
          </Container>
        )}
        <EditActions onCancel={onCancel} onEditEnd={onEditEnd} isSaveDisabled={isDisabled()} />
      </Container>
    </Stack>
  );
};
